import mixpanel from '@disco/api/mixpanel'
import type {
  CatalogPropertyTypes,
  DiscoverClickTypes,
  DiscoverPlayAudioTypes,
  DiscoverSearchTypes,
  DragAndDropTrackProperties,
  PlaylistPropertyTypes,
} from './types'

const LOCATION = 'Discover Music'

export const getMappedCatalogOrPlaylistMixpanelProperties = (
  props: CatalogPropertyTypes &
    PlaylistPropertyTypes & { trackId?: number; trackName?: string },
) => {
  const base = {
    ...(props?.trackId && { 'Track ID': props.trackId }),
    ...(props?.trackName && { 'Track Name': props.trackName }),
  }

  if (props?.catalogId) {
    return {
      ...base,
      Catalog: props.catalogId,
      'Catalog Name': props.catalogName || 'Untitled Catalog',
      'Catalog Business': props.catalogBusiness || 'Unknown',
      ...(props?.isFeatured !== undefined && {
        'Featured Catalog': props.isFeatured,
      }),
    }
  }

  if (props?.playlistId) {
    return {
      ...base,
      'Playlist ID': props.playlistId,
      Playlist: props.playlist || 'Untitled Playlist',
      'Playlist Business': props.playlistBusiness || 'Unknown',
      ...(props.playlistBusinessId !== undefined && {
        'Playlist Business ID': props.playlistBusinessId,
      }),
    }
  }

  return base
}

export const sendDiscoverSearchMixpanelEvent = (props: DiscoverSearchTypes) => {
  mixpanel.sendPureEvent('Search', {
    Context: props.context,
    'Search Term': props.searchTerm,
    Filter: props.filter,
    'Result Count': props.result_count,
    Location: props.location,
  })
}

export const sendDiscoverClickImageMixpanelEvent = (
  props: DiscoverClickTypes,
) => {
  mixpanel.sendPureEvent('Click Image', {
    Context: props.context,
    ...getMappedCatalogOrPlaylistMixpanelProperties(props),
    Type: props?.catalogId ? 'Open Catalog Page' : 'Open Playlist Page',
    Location: LOCATION,
  })
}

export const getDiscoverPlayAudioMixpanelProperties = (
  props: DiscoverPlayAudioTypes,
) => {
  return {
    Context: props.context,
    'Track ID': props.trackId,
    ...getMappedCatalogOrPlaylistMixpanelProperties(props),
    Location: LOCATION,
  }
}

export const sendDiscoverClickButtonMixpanelEvent = (
  props: DiscoverClickTypes,
) => {
  mixpanel.sendPureEvent('Click Button', {
    Context: props.context,
    Copy: props?.copy,
    ...getMappedCatalogOrPlaylistMixpanelProperties(props),
    Type: props?.catalogId ? 'Open Catalog Page' : 'Open Playlist Page',
    Location: LOCATION,
  })
}

export const getDiscoverDragAndDropTrackProperties = (
  props: DragAndDropTrackProperties,
  type: string,
) => {
  return {
    Context: props.context,
    'Track ID': props.trackId,
    ...getMappedCatalogOrPlaylistMixpanelProperties(props),
    Location: LOCATION,
    Type: type,
  }
}

type EventPayloadMap = {
  Search: DiscoverSearchTypes
  'Click Image': DiscoverClickTypes
  'Click Button': DiscoverClickTypes
}
export type DiscoverEventType = keyof EventPayloadMap

const mixpanelEventHandlers: {
  [T in DiscoverEventType]: (data: EventPayloadMap[T]) => void
} = {
  Search: sendDiscoverSearchMixpanelEvent,
  'Click Image': sendDiscoverClickImageMixpanelEvent,
  'Click Button': sendDiscoverClickButtonMixpanelEvent,
}

export const sendDiscoverMixpanelEvent = <T extends DiscoverEventType>(
  eventType: T,
  data: EventPayloadMap[T],
) => mixpanelEventHandlers[eventType](data)
